/**
 * cabinetTime.ts — время кабинета: единый часовой пояс и формат даты-времени
 * для таблиц (PM 2026-07-25).
 *
 * Пояс задаётся в Настройках → Персонализация (`timezone` + `timezoneAuto`,
 * P1 §F8). При автоопределении берём пояс браузера — ровно то же, что
 * показывает подсказка в настройках.
 *
 * Формат «дд/мм/гггг - чч:мм» — дневная часть как в таблицах «Контента»
 * (там дата без времени), плюс часы и минуты, потому что в аудите важна
 * точность до минуты.
 *
 * WEB-1874: строка локали больше не зашита здесь — форматтер берётся у
 * `i18n/format.ts` по языку кабинета. Сама строка от языка не зависит: части
 * запрошены двузначными числами и собираются нами, поэтому русский и
 * английский дают одинаковые «17/07/2026 - 13:42».
 */
import type { Dayjs } from 'dayjs';
import { dateTimeFormat } from '@/i18n';

export interface CabinetTimeUser {
  timezone?: string;
  timezoneAuto?: boolean;
}

function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Часовой пояс кабинета: ручной выбор пользователя либо пояс браузера. */
export function cabinetTimezone(user: CabinetTimeUser | null | undefined): string {
  if (!user) return browserTimezone();
  const auto = user.timezoneAuto ?? true;
  if (auto || !user.timezone) return browserTimezone();
  return user.timezone;
}

/**
 * Форма имени пояса, которую принимает сервер отчётов (WEB-2404 пп.3, 9):
 * «Область/Место» (`Europe/Moscow`, `America/Argentina/Buenos_Aires`) либо
 * `UTC`. Та же форма, что у `assertValidIanaTimezone` сервера
 * (`backend/.../sql-fragments/event-time.ts`): однословные старые имена
 * (`GMT`, `Japan`) и смещения со знаком (`+03`) сервер отклоняет отказом 400.
 */
const REPORT_ZONE_SHAPE = /^[A-Za-z]+(\/[A-Za-z0-9_+-]+){1,2}$/;

/**
 * ПОЯС ОТЧЁТА — ОДНА дверь для параметра `tz` ручек отчётов («По дням»,
 * матрица когорт, сводка и график организации) и для границ периода этих
 * экранов (WEB-2404 пп.3, 9). Пояс кабинета (`cabinetTimezone`), если сервер
 * его примет; иначе `UTC` — экран не получает отказ и не падает, а сутки
 * режутся так же, как без параметра. Сторож двух концов:
 * `src/__tests__/reportTimezone.twoEnds.web2404.guard.test.ts`.
 */
export function reportTimezone(user: CabinetTimeUser | null | undefined): string {
  const zone = cabinetTimezone(user);
  if (zone === 'UTC') return zone;
  if (!REPORT_ZONE_SHAPE.test(zone)) return 'UTC';
  try {
    dateTimeFormat({ timeZone: zone });
  } catch {
    return 'UTC';
  }
  return zone;
}

/** Начало календарного дня (год, месяц 1–12, день) в поясе — момент в мс. */
function zoneDayStartMs(year: number, month: number, day: number, timezone: string): number {
  const guess = Date.UTC(year, month - 1, day);
  // Смещение пояса в момент, близкий к искомому; второй проход — на случай,
  // если полночь по разные стороны перехода на летнее время.
  let ms = guess;
  for (let i = 0; i < 2; i += 1) {
    const c = cabinetClock(new Date(ms), timezone);
    if (!c) return guess;
    const wall = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute);
    ms = guess - (wall - ms);
  }
  // Полночи может не быть: часы переводят ровно в 00:00 (Гавана, Сантьяго,
  // Азоры — 00:00 → 01:00), и найденный момент выпадает на 23:00 ПРЕДЫДУЩЕГО
  // дня. Тогда сутки начинаются первым существующим мгновением нужного дня —
  // моментом перевода; ищем его делением отрезка до миллисекунды.
  const target = Date.UTC(year, month - 1, day);
  const dayOf = (at: number): number => {
    const c = cabinetClock(new Date(at), timezone);
    return c ? Date.UTC(c.year, c.month - 1, c.day) : target;
  };
  if (dayOf(ms) >= target && dayOf(ms - 1) < target) return ms;
  let lo = dayOf(ms) < target ? ms : ms - 26 * 3_600_000;
  let hi = dayOf(ms) < target ? ms + 26 * 3_600_000 : ms;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (dayOf(mid) >= target) hi = mid;
    else lo = mid;
  }
  return hi;
}

/** «2026-09-01» — календарный день момента в поясе; мусор — `null`. */
export function cabinetDayOfIso(iso: string, timezone: string): string | null {
  const c = cabinetClock(iso, timezone);
  if (!c || Number.isNaN(c.year)) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${c.year}-${pad(c.month)}-${pad(c.day)}`;
}

function dayBoundsIso(
  from: { year: number; month: number; day: number },
  to: { year: number; month: number; day: number },
  timezone: string,
): { from: string; to: string } {
  const start = zoneDayStartMs(from.year, from.month, from.day, timezone);
  const end = zoneDayStartMs(to.year, to.month, to.day + 1, timezone) - 1;
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

/**
 * Границы суток для запроса периода «с дня А по день Б» (WEB-2415 п.2): оба
 * дня целиком — от начала первых суток до конца последних.
 *
 * С поясом (WEB-2404 п.3): дни — календарные даты, выбранные в календаре, а
 * их сутки режутся в этом поясе — в том же, что уходит серверу параметром
 * `tz` (`reportTimezone`). Без пояса — прежнее правило, сутки БРАУЗЕРА; живых
 * вызовов без пояса в экранах нет: «Аудит → Трафик» и «Аудит → Команда»
 * переведены на пояс отчёта ревизией пачки 49 (находка 4) — их ручки параметра
 * tz не принимают, но границы приходят готовыми моментами времени.
 */
export function cabinetDayRangeIso(
  from: Dayjs,
  to: Dayjs,
  timezone?: string,
): { from: string; to: string } {
  if (!timezone) {
    return { from: from.startOf('day').toISOString(), to: to.endOf('day').toISOString() };
  }
  return dayBoundsIso(
    { year: from.year(), month: from.month() + 1, day: from.date() },
    { year: to.year(), month: to.month() + 1, day: to.date() },
    timezone,
  );
}

/**
 * «Последние N дней» включая сегодня — по календарю пояса отчёта (WEB-2404
 * п.3): «Сегодня» у владельца в Токио — токийское сегодня, даже когда по
 * Гринвичу ещё вчера.
 */
export function cabinetLastDaysIso(
  days: number,
  timezone: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const today = cabinetClock(now, timezone);
  if (!today) return { from: now.toISOString(), to: now.toISOString() };
  return dayBoundsIso(
    { year: today.year, month: today.month, day: today.day - (days - 1) },
    today,
    timezone,
  );
}

/**
 * Прошлый период для сравнения (WEB-2616): те же N календарных дней пояса,
 * сдвинутые назад, — сутки прошлого периода режутся по тем же часам пояса,
 * и сутки по 23 и 25 часов на переводе часов входят целиком. Период не из
 * целых суток пояса (начало не в начале суток, конец не в последней
 * миллисекунде суток) или мусор — `null`: вызывающий решает сам, что делать.
 */
export function cabinetPreviousDaysIso(
  fromIso: string,
  toIso: string,
  timezone: string,
): { from: string; to: string } | null {
  const fromDay = cabinetClock(fromIso, timezone);
  const toDay = cabinetClock(toIso, timezone);
  if (!fromDay || !toDay || Number.isNaN(fromDay.year) || Number.isNaN(toDay.year)) return null;
  const current = dayBoundsIso(fromDay, toDay, timezone);
  if (current.from !== new Date(fromIso).toISOString() || current.to !== new Date(toIso).toISOString()) {
    return null;
  }
  const days =
    (Date.UTC(toDay.year, toDay.month - 1, toDay.day) - Date.UTC(fromDay.year, fromDay.month - 1, fromDay.day)) /
      86_400_000 +
    1;
  if (!(days >= 1)) return null;
  return dayBoundsIso(
    { year: fromDay.year, month: fromDay.month, day: fromDay.day - days },
    { year: fromDay.year, month: fromDay.month, day: fromDay.day - 1 },
    timezone,
  );
}

/** «17/07/2026 - 13:42». Нераспознанная дата → «—», а не «Invalid Date». */
export function formatCabinetDateTime(iso: string | null | undefined, timezone: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = dateTimeFormat({
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} - ${get('hour')}:${get('minute')}`;
}

/** «Стенные часы» момента в поясе кабинета; `month` — 1–12. */
export interface CabinetClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const CLOCK_PARTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  // Полночь — «0», а не «24» (см. `i18n/format.ts`, ревизия пачки 15).
  hourCycle: 'h23',
};

/**
 * Части момента в поясе кабинета — ОДНА дверь для экранов, которые собирают
 * дату и время сами (журнал команды — WEB-2467 п.5.1, журнал списаний —
 * WEB-2536). Пояс, которого не знает ICU этого браузера (старое имя, опечатка
 * в профиле), не роняет экран: части берутся по поясу браузера. Неразбираемая
 * дата — `null`.
 */
export function cabinetClock(at: Date | string, timezone: string): CabinetClock | null {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  let format: Intl.DateTimeFormat;
  try {
    format = dateTimeFormat({ ...CLOCK_PARTS, timeZone: timezone });
  } catch {
    format = dateTimeFormat(CLOCK_PARTS);
  }
  const parts = format.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? NaN);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}
